import { formatMoney } from "../app_js/utils/money";

/**
 * Price for a given learner count, from the two linear parts the server sends: `fixed` is charged
 * once, `per_trainee` once per learner.
 *
 * @param {{ fixed: number, per_trainee: number }} parts
 * @param {number} count
 * @returns {number} amount rounded to the cent
 */
export function computePrice(parts, count) {
  const learners = Number.isInteger(count) && count > 0 ? count : 1;

  return Math.round((parts.fixed + parts.per_trainee * learners) * 100) / 100;
}

/**
 * Text of a session option for a given learner count: the label the server rendered without its
 * price, then the translated price label with both amounts filled in.
 *
 * @param {{ label: string, parts: { no_vat: object, with_vat: object }, priceLabel: string }} option
 * @param {number} count
 * @param {string} moneyCode
 * @returns {string}
 */
export function sessionOptionText({ label, parts, priceLabel }, count, moneyCode) {
  const price = priceLabel
    .replace("%{price_without_vat}", formatMoney(computePrice(parts.no_vat, count), moneyCode))
    .replace("%{price_with_vat}", formatMoney(computePrice(parts.with_vat, count), moneyCode));

  return `${label} - ${price}`;
}

/**
 * Keep every displayed price in step with the learner count: the amount on the self-served purchase
 * button and the price of each option in the session select.
 *
 * The count has two sources. The declarative count input is listened to here, since it renders
 * whether or not the nominative trainee rows do. The rows live in the `companyTraineeRegistration`
 * Alpine component, which announces their count through `learner-count-changed`. The server-rendered
 * prices stay in place until the first change, so the page reads the same without JS.
 */
export function initLivePrice() {
  const buttons = document.querySelectorAll(".js-live-price");
  const selects = document.querySelectorAll(".js-registration-date[data-price-label]");
  if (buttons.length === 0 && selects.length === 0) return;

  const update = (count) => {
    for (const button of buttons) {
      const parts = JSON.parse(button.dataset.priceParts);
      const amount = button.querySelector(".js-live-price-amount");

      amount.textContent = formatMoney(computePrice(parts, count), button.dataset.moneyCode);
    }

    for (const select of selects) {
      const { moneyCode, priceLabel } = select.dataset;

      for (const option of select.querySelectorAll("option[data-price-parts]")) {
        const { label, priceParts } = option.dataset;

        option.textContent = sessionOptionText({ label, parts: JSON.parse(priceParts), priceLabel }, count, moneyCode);
      }
    }
  };

  document.addEventListener("learner-count-changed", (e) => update(e.detail.count));
  document.querySelector(".js-learner-registration-count")?.addEventListener("input", (e) => update(parseInt(e.target.value)));
}
